























































































/**
 * View for 'backup and restore' tab
 */
import Vue from 'vue';
import BackupsDataTable from '@/views/Settings/BackupAndRestore/BackupsDataTable.vue';
import { Permissions } from '@/global';
import * as api from '@/util/api';
import { Backup, BackupExt } from '@/openapi-types';
import { FileUploadResult } from '@niis/shared-ui';
import { mapActions, mapState } from 'pinia';
import { useUser } from '@/store/modules/user';
import { useNotifications } from '@/store/modules/notifications';

const uploadBackup = (backupFile: File, ignoreWarnings = false) => {
  const formData = new FormData();
  formData.set('file', backupFile, backupFile.name);
  return api.post(
    `/backups/upload?ignore_warnings=${ignoreWarnings}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export default Vue.extend({
  components: {
    BackupsDataTable,
  },
  data() {
    return {
      search: '' as string,
      creatingBackup: false,
      uploadingBackup: false,
      needsConfirmation: false,
      uploadedFile: null as File | null,
      backups: [] as Backup[],
      loadingBackups: false,
    };
  },
  computed: {
    ...mapState(useUser, ['hasPermission']),
    canBackup(): boolean {
      return this.hasPermission(Permissions.BACKUP_CONFIGURATION);
    },
  },
  created(): void {
    this.fetchData();
  },
  methods: {
    ...mapActions(useNotifications, [
      'showError',
      'showSuccess',
      'showWarningMessage',
    ]),
    async fetchData() {
      this.loadingBackups = true;
      return api
        .get<Backup[]>('/backups')
        .then((res) => {
          this.backups = res.data.sort((a, b) => {
            return b.created_at.localeCompare(a.created_at);
          });
        })
        .catch((error) => this.showError(error))
        .finally(() => (this.loadingBackups = false));
    },
    async createBackup() {
      this.creatingBackup = true;
      return api
        .post<BackupExt>('/backups/ext', null)
        .then((resp) => {
          if (resp.data.local_conf_present) {
            this.showWarningMessage(
              this.$t('backup.backupConfiguration.message.localConfWarning'),
            );
          }
          this.showSuccess(
            this.$t('backup.backupConfiguration.message.success', {
              file: resp.data.backup.filename,
            }),
          );
          this.fetchData();
        })
        .catch((error) => this.showError(error))
        .finally(() => (this.creatingBackup = false));
    },
    onFileUploaded(result: FileUploadResult): void {
      this.uploadingBackup = true;
      this.uploadedFile = result.file;
      uploadBackup(result.file)
        .then(() => {
          this.fetchData();
          this.showSuccess(
            this.$t('backup.uploadBackup.success', {
              file: this.uploadedFile?.name,
            }),
          );
        })
        .catch((error) => {
          const warnings = error.response?.data?.warnings as Array<{
            code: string;
          }>;
          if (
            error.response?.status === 400 &&
            warnings?.some(
              (warning) => warning.code === 'warning_file_already_exists',
            )
          ) {
            this.needsConfirmation = true;
            return;
          }
          this.showError(error);
        })
        .finally(() => (this.uploadingBackup = false));
    },
    async overwriteBackup() {
      this.uploadingBackup = true;
      // this will only be called if the file has already been uploaded once and got warnings
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return uploadBackup(this.uploadedFile!, true)
        .then(() => {
          this.fetchData();
          this.showSuccess(
            this.$t('backup.uploadBackup.success', {
              file: this.uploadedFile?.name,
            }),
          );
        })
        .catch((error) => this.showError(error))
        .finally(() => {
          this.uploadingBackup = false;
          this.needsConfirmation = false;
        });
    },
  },
});
